import { Matrix3, Vector2 } from 'three';

/**
 *
 * @param {import('../Materials/PBR/PBR.js').default} material
 * @param {string} key
 */
export function textureUvTransform(material, key) {
	const matrix = createTextureMatrix(material, key);
	setShaderProp(material, key + 'Transform', matrix);

	const uvTransform = {
		/** @type {THREE.Vector2} */
		repeat: material.params[key + 'TransformRepeat'],
		/** @type {THREE.Vector2} */
		offset: material.params[key + 'TransformOffset'],
		/** @type {THREE.Vector2} */
		center: material.params[key + 'TransformCenter'],
		/** @type {number} */
		rotation: material.params[key + 'TransformRotation'],
		/** @type {THREE.Matrix3} */
		matrix,
		update() {
			this.matrix.setUvTransform(
				this.offset.x,
				this.offset.y,
				this.repeat.x,
				this.repeat.y,
				this.rotation,
				this.center.x,
				this.center.y,
			);

			return this;
		},
	};

	return uvTransform.update();
}

/**
 *
 * @param {import('../Materials/PBR/PBR.js').default} material
 * @param {string} key
 */
export function createTextureMatrix(material, key) {
	const texture = material.params[key];

	const uvTransform = {
		repeat: new Vector2(),
		offset: new Vector2(),
		center: new Vector2(),
		rotation: 0,
	};

	const repeat = material.params[key + 'TransformRepeat'];
	const offset = material.params[key + 'TransformOffset'];
	const center = material.params[key + 'TransformCenter'];
	const rotation = material.params[key + 'TransformRotation'];

	uvTransform.repeat.set(repeat?.x || texture.repeat.x, repeat?.y || texture.repeat.y);
	uvTransform.offset.set(offset?.x || texture.offset.x, offset?.y || texture.offset.y);
	uvTransform.center.set(center?.x || texture.center.x, center?.y || texture.center.y);
	uvTransform.rotation = rotation || texture.rotation;

	material.params[key + 'TransformRepeat'] = uvTransform.repeat;
	material.params[key + 'TransformOffset'] = uvTransform.offset;
	material.params[key + 'TransformCenter'] = uvTransform.center;
	material.params[key + 'TransformRotation'] = uvTransform.rotation;

	return new Matrix3().setUvTransform(
		uvTransform.offset.x,
		uvTransform.offset.y,
		uvTransform.repeat.x,
		uvTransform.repeat.y,
		uvTransform.rotation,
		uvTransform.center.x,
		uvTransform.center.y,
	);
}

/**
 * @param {import('../Materials/PBR/PBR.js').default} material
 * @param {number} channel
 */
export function channelToUvString(material, channel) {
	if (channel === 0) {
		// material.defines['USE_UV'] = 1;
		return 'uv';
	} else {
		// material.defines['USE_UV' + channel] = 1;
		return 'uv' + channel;
	}
}

/**
 *
 * @param {import('../Materials/PBR/PBR.js').default} material
 * @param {string} key
 * @param {boolean} [needsUv]
 */
export function bindUniform(material, key, needsUv = false) {
	Object.defineProperty(material, key, {
		set: (value) => {
			material[`_${key}`] = value;

			setShaderProp(material, key, value, needsUv);
		},
		get: () => {
			return material[`_${key}`];
		},
	});
}

/**
 *
 * @param {import('../Materials/PBR/PBR.js').default} material
 * @param {string} key
 * @param {*} value
 * @param {boolean} [needsUv]
 */
export function setShaderProp(material, key, value, needsUv = false) {
	if (value == null) return;

	const uniformKey = (value?.isTexture ? 't' : 'u') + (key.charAt(0).toUpperCase() + key.slice(1));

	if (material.uniforms[uniformKey]) material.uniforms[uniformKey].value = value;
	else {
		if (value?.isTexture) {
			const defineKey = 'SIXTY_' + key.toUpperCase();
			material.defines[defineKey] = 1;

			if (needsUv) {
				material.defines[defineKey + '_UV'] = channelToUvString(material, value.channel);
				material[key + 'Transform'] = textureUvTransform(material, key);
			}
		}
		material.uniforms[uniformKey] = { value };
		material.needsUpdate = true;
	}
}

/**
 *
 * @param {*} value
 * @param {*} updateCb
 */
export function monitorVector(value, updateCb = () => {}) {
	if (value.x != null) {
		Object.defineProperty(value, 'x', {
			get() {
				return this._x;
			},
			set(value) {
				this._x = value;
				updateCb();
			},
		});
	}
	if (value.y != null) {
		Object.defineProperty(value, 'y', {
			get() {
				return this._y;
			},
			set(value) {
				this._y = value;
				updateCb();
			},
		});
	}

	if (value.z != null) {
		Object.defineProperty(value, 'z', {
			get() {
				return this._z;
			},
			set(value) {
				this._z = value;
				updateCb();
			},
		});
	}

	if (typeof value.copy === 'function') {
		const copyFn = value.copy;
		value.copy = function (target) {
			const result = copyFn.call(this, target);
			updateCb();
			return result;
		};
	}

	updateCb();
}
